import { useMemo } from 'react'
import { orderBy } from 'lodash-es'
import { useResultsStore } from '@editorStores'
import { AnchorSide } from '../utils'
import useAnchorSides from './useAnchorSides'

export interface SegmentAnchor {
  supportGuid: string
  side: AnchorSide
}

/**
 * The tensile anchors sitting on one stiffening segment, left one first.
 *
 * Lets the Wandscheiben, Anker and Schubanker panes link to each other's anchors without each
 * re-deriving which support belongs to which segment and which side it is on.
 */
const useAnchorsOfSegment = (segmentGuid?: string | null): SegmentAnchor[] => {
  const anchorInterventionTableData = useResultsStore(state => state.anchorInterventionTableData)
  const anchorSides = useAnchorSides()

  return useMemo(() => {
    if (!segmentGuid) return []
    const anchors = (anchorInterventionTableData ?? [])
      .filter(row => row.segment_guid === segmentGuid)
      .map(row => ({ supportGuid: row.support_guid, side: anchorSides[row.support_guid] }))
      .filter((anchor): anchor is SegmentAnchor => !!anchor.side)
    // 'links' before 'rechts' so the links always read in the same order.
    return orderBy(anchors, anchor => (anchor.side === 'rechts' ? 1 : 0), 'asc')
  }, [anchorInterventionTableData, anchorSides, segmentGuid])
}

export default useAnchorsOfSegment
